/**
 * ProvaDeDadosSection — Onda 2 do plano de qualidade.
 *
 * Mostra, na Conferência de Entrega, a matriz "entidade × operação × regra": cada
 * obrigação derivada do esquema REAL do banco (após as migrations) ou de um critério
 * de aceite (`REQ.<uuid>.cN`), com o status vindo da execução real da suíte de
 * integração (JUnit) + escritas reais no banco. Nada aqui vem de LLM.
 *
 * `prova` null/undefined → não renderiza nada.
 *
 * Standards: Clean, TDD.
 */
import { useMemo, useState } from 'react';
import { C } from '@/theme/palette';
import { useExpandirTudo } from '@/components/ExportacaoContext';
import {
  GruposConferencia, NOTA_COMPLEMENTARES, COR_GRUPO, GRUPO_COMPLEMENTAR, GRUPO_CORRECAO,
  grupoDoItem, contarGrupos,
} from '@/components/GruposConferencia';

/** Rótulo e cor de cada status (paleta C; `dim` = neutralDim da paleta). */
export const STATUS_PROVA = {
  passed:         { label: 'provada',                   color: C.cyber },
  failed:         { label: 'falhando',                  color: C.hot },
  missing:        { label: 'sem teste',                 color: C.neon },  // atividade complementar
  nao_usou_banco: { label: 'passou sem tocar o banco',  color: C.warn },
  nao_conferido:  { label: 'não conferida',             color: C.neutralDim },
  trivial:        { label: 'trivial',                   color: C.neutralDim },
};

const ORDEM_STATUS = ['passed', 'failed', 'missing', 'nao_usou_banco', 'nao_conferido', 'trivial'];
export const GRUPO_CRITERIOS = 'Critérios de aceite';

function infoStatus(status) {
  return STATUS_PROVA[status] ?? { label: status || 'desconhecido', color: C.neutralDim };
}

function isCriterio(item) {
  return item?.kind === 'criterio' || String(item?.id ?? '').startsWith('REQ.');
}

/** Agrupa por entidade (tabela); critérios de aceite num grupo próprio, sempre no fim. */
export function agruparPorEntidade(itens) {
  const grupos = new Map();
  for (const it of itens) {
    const chave = isCriterio(it) ? GRUPO_CRITERIOS : (it.entity || 'sem entidade');
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave).push(it);
  }
  return [...grupos.entries()].sort(([a], [b]) => {
    if (a === GRUPO_CRITERIOS) return 1;
    if (b === GRUPO_CRITERIOS) return -1;
    return a.localeCompare(b, 'pt-BR');
  });
}

function Chip({ status, count }) {
  const { label, color } = infoStatus(status);
  return (
    <span data-testid={`chip-${status}`} style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 10px',
      borderRadius: 6, color, background: `${color}1F`, border: `1px solid ${color}55` }}>
      {Number(count).toLocaleString('pt-BR')} {label}
    </span>
  );
}

function LinhaObrigacao({ item }) {
  const { label, color } = infoStatus(item.status);
  const tests = item.tests ?? [];
  const writes = Number(item.dbWrites ?? 0);
  return (
    <div data-testid="obrigacao" style={{ padding: '8px 16px 8px 34px', borderTop: `1px solid ${C.blackBorder}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: C.text, wordBreak: 'break-all' }}>{item.id}</span>
        <span style={{ fontFamily: 'monospace', fontSize: 10.5, padding: '2px 8px', borderRadius: 5,
          color, background: `${color}1F`, border: `1px solid ${color}55` }}>{label}</span>
        {grupoDoItem(item) === GRUPO_COMPLEMENTAR && (
          <span data-testid="tag-complementar" style={{ fontSize: 10.5, color: COR_GRUPO.complementar }}>
            atividade complementar
          </span>
        )}
        {item.kind && <span style={{ fontSize: 11, color: C.neutralMuted }}>{item.kind}</span>}
        <span style={{ fontSize: 11.5, color: C.neutralDim, marginLeft: 'auto' }}>
          {writes.toLocaleString('pt-BR')} escrita(s)
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: C.neutralDim, marginTop: 4 }}>
        {tests.length
          ? <>Testes: <span style={{ fontFamily: 'monospace' }}>{tests.join(', ')}</span></>
          : <span style={{ color: C.neutralMuted }}>nenhum teste associado</span>}
        {item.repo && <span style={{ color: C.neutralMuted }}> · {item.repo}</span>}
      </div>
      {item.status !== 'passed' && item.evidence && (
        <div data-testid="evidencia" style={{ fontSize: 11.5, color: C.neutralDim, marginTop: 4,
          fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          {item.evidence}
        </div>
      )}
    </div>
  );
}

function Grupo({ nome, itens }) {
  const [escolhaAberto, setAberto] = useState(false);
  const expandirTudo = useExpandirTudo();
  const aberto = escolhaAberto || expandirTudo;
  const provadas = itens.filter((i) => i.status === 'passed').length;
  const correcoes = itens.filter((i) => grupoDoItem(i) === GRUPO_CORRECAO).length;
  const corResumo = correcoes ? C.warn : (provadas < itens.length ? COR_GRUPO.complementar : C.cyber);
  return (
    <div data-testid="grupo-prova" style={{ borderTop: `1px solid ${C.blackBorder}` }}>
      <button type="button" onClick={() => setAberto((v) => !v)} aria-expanded={aberto}
        data-export="estatico"
        style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
          width: '100%', boxSizing: 'border-box', padding: '10px 16px' }}>
        <span style={{ color: C.neutralMuted, width: 12 }}>{aberto ? '▾' : '▸'}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text,
          fontFamily: nome === GRUPO_CRITERIOS ? 'inherit' : 'monospace' }}>{nome}</span>
        <span style={{ fontSize: 11.5, color: corResumo, marginLeft: 'auto' }}>
          {provadas.toLocaleString('pt-BR')}/{itens.length.toLocaleString('pt-BR')} provadas
        </span>
      </button>
      {aberto && itens.map((it) => <LinhaObrigacao key={it.id} item={it} />)}
    </div>
  );
}

export function ProvaDeDadosSection({ prova }) {
  const itens = useMemo(() => prova?.itens ?? [], [prova]);
  const temProblema = itens.some((i) => i.status !== 'passed');
  // Padrão segue os dados (ligado se houver algo não provado) até o usuário mexer.
  const [escolha, setSoProblemas] = useState(null);
  // Exportação: tudo, sem o filtro (a escolha do usuário fica guardada).
  const expandirTudo = useExpandirTudo();
  const soProblemas = expandirTudo ? false : (escolha ?? temProblema);

  const grupos = useMemo(
    () => agruparPorEntidade(soProblemas ? itens.filter((i) => i.status !== 'passed') : itens),
    [itens, soProblemas],
  );

  if (!prova) return null;

  const total = Number(prova.total ?? itens.length);
  const porStatus = prova.porStatus ?? {};
  const provadas = Number(porStatus.passed ?? itens.filter((i) => i.status === 'passed').length);
  const porGrupo = prova.porGrupo ?? contarGrupos(itens);

  return (
    <div data-testid="prova-de-dados" style={{ marginTop: 16, border: `1px solid ${C.blackBorder}`,
      background: C.blackElev, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: C.cyber }}>⛁</span>
          <h3 style={{ margin: 0, fontSize: 14, color: C.text }}>Prova de dados · entidade × operação × regra</h3>
        </div>
        <p data-testid="prova-resumo" style={{ fontSize: 13, color: C.text, margin: '8px 0 6px', fontWeight: 600 }}>
          {provadas.toLocaleString('pt-BR')} de {total.toLocaleString('pt-BR')} obrigações provadas contra o banco real
        </p>
        <div data-testid="prova-grupos" style={{ marginBottom: 6 }}>
          <GruposConferencia contagem={porGrupo} rotuloOk="provadas" />
        </div>
        {Number(porGrupo.complementares ?? 0) > 0 && (
          <p style={{ fontSize: 12, color: C.neutralDim, margin: '0 0 6px' }}>{NOTA_COMPLEMENTARES}</p>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ORDEM_STATUS.filter((s) => Number(porStatus[s] ?? 0) > 0)
            .map((s) => <Chip key={s} status={s} count={porStatus[s]} />)}
        </div>
        <p style={{ fontSize: 12, color: C.neutralMuted, margin: '8px 0 0' }}>
          Status vindo da execução real da suíte de integração contra o banco do blueprint — não da IA.
        </p>
        <label data-export="omitir" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
          fontSize: 12, color: C.neutralDim, cursor: 'pointer' }}>
          <input type="checkbox" checked={soProblemas} onChange={(e) => setSoProblemas(e.target.checked)} />
          mostrar só o que não passou
        </label>
      </div>
      {grupos.length === 0 ? (
        <div style={{ padding: '10px 16px 14px', fontSize: 12, color: C.neutralMuted,
          borderTop: `1px solid ${C.blackBorder}` }}>
          {soProblemas ? 'Todas as obrigações passaram.' : 'Nenhuma obrigação registrada.'}
        </div>
      ) : (
        grupos.map(([nome, lista]) => <Grupo key={nome} nome={nome} itens={lista} />)
      )}
    </div>
  );
}

export default ProvaDeDadosSection;
